import type { Modifier } from '../modifier.js';
import { hasTag } from '../token.js';
import type { ItemDef } from './defs.js';

/**
 * THE HOUSE'S SIDE OF THE CATALOGUE — ZINKEN and Manipulationen.
 *
 * house-and-heat.md's central claim is that the House plays by the player's grammar. That
 * is not a slogan here: every entry below is an ordinary `Modifier`, resolved by the same
 * pipeline in the same priority order as your charms, recorded on the same Hausregeln
 * board. There is no second code path for the House and there is no scripted cheat
 * anywhere in this game.
 *
 * Three laws, all mechanical rather than aspirational:
 *
 * 1. **Readable once discovered.** ZINKEN ship with `discovered: false`, which the board
 *    renders as a crooked "?" stamp. Play the cabinet once, or spend GLÜCK to inspect.
 * 2. **Exploitable by construction.** Because ZINKEN speak tags, player items interact
 *    with them. Every entry names its counter in `counter`, and the counter is a real
 *    interaction in the hook below — not a note. The Magnetfalle genuinely inverts under
 *    a [Magnet] enchant, because the same query answers both.
 * 3. **Stealable.** A Brecheisen prises a discovered ZINKEN off the cabinet and puts it in
 *    your bag as a charm. The House's rigging becomes yours, machines and all.
 */

/** Marks the arc: floor 2 you suffer it, floor 6 you own it. */
export interface ZinkenDef extends ItemDef {
  kind: 'zinken';
  /** the player-facing counterplay, printed under the crooked stamp once discovered */
  counter: string;
}

const zinken = (
  id: string,
  name: string,
  text: string,
  counter: string,
  modifier: Modifier,
  combos: string[],
  nombos: string[],
): ZinkenDef => ({
  id, name, kind: 'zinken', rarity: 'rar', text, counter, combos, nombos,
  modifier: { ...modifier, source: 'zinken', discovered: false },
});

export const ZINKEN: ZinkenDef[] = [
  zinken(
    'zinken:magnetfalle',
    'Magnetfalle',
    'Abstoßspulen an den Taschen: Marken scheuen die Auszahlung.',
    'Eine [Magnet]-Marke dreht die Spule um — die Falle zieht dann für Sie.',
    {
      id: 'zinken:magnetfalle',
      source: 'zinken',
      tags: ['Magnet'],
      priority: 210,
      ruleText: 'Magnetfalle: die Taschenmäuler ziehen sich zusammen.',
      queries: {
        /**
         * The counter is the same query answering twice. A bare token sees narrowed
         * pocket mouths; a [Magnet] token sees the coils fighting its own field and
         * losing, so the mouths open wider than they started. One rule, two readings.
         */
        pocketWidth: (ctx, v) => (ctx.token && hasTag(ctx.token, 'Magnet') ? v * 1.35 : v * 0.62),
      },
    },
    ['kassierter-fluch'],
    ['magnetschuh'],
  ),

  zinken(
    'zinken:gewichtete-kugel',
    'Gewichtete Kugel',
    'Die Kugel meidet die Fächer, in denen Ihre Marken liegen.',
    'Setzen Sie überall ([Spalt] teilt Ihre Einsätze) — oder machen Sie das Meiden bezahlt ([Fluch]).',
    {
      id: 'zinken:gewichtete-kugel',
      source: 'zinken',
      tags: ['Magnet'],
      priority: 212,
      machines: ['roulette'],
      ruleText: 'Gewichtete Kugel: der Kessel meidet Ihre Fächer.',
      queries: {
        // ROULETTE reads `pocketWidth` as its landing bias: 1 neutral, <0 repelled. A
        // [Magnet] ball overpowers the weight; with no magnet, the ball shies away.
        pocketWidth: (ctx, v) => (ctx.token && hasTag(ctx.token, 'Magnet') ? v : -Math.abs(v) * 0.8),
      },
    },
    ['spaltmuenze'],
    ['magnetschuh'],
  ),

  zinken(
    'zinken:mueder-greifer',
    'Müder Greifer',
    'Der Griff der Klaue erlahmt mit jedem Versuch.',
    'Schwere Marken drücken die Klaue zu; eine [Geist]-Marke greift durch das Glas statt in den Schacht.',
    {
      id: 'zinken:mueder-greifer',
      source: 'zinken',
      tags: ['Schwer'],
      priority: 214,
      machines: ['greifer'],
      ruleText: 'Müder Greifer: der Griff lässt mit der Zeit nach.',
      queries: {
        odds: (ctx, p) => {
          if (ctx.stream !== 'greifer') return p;
          // decays over the session, floored so it never becomes unwinnable
          const minutes = ctx.tick / (120 * 60);
          const decay = Math.max(0.45, 1 - minutes * 0.5);
          return ctx.token && hasTag(ctx.token, 'Schwer') ? p : p * decay;
        },
      },
    },
    ['zinken:die-kamera'],
    ['schwermuenze', 'geistermuenze'],
  ),

  zinken(
    'zinken:gezinkte-walze',
    'Gezinkte Walze',
    'Eine Walze ist gegen Ihr häufigstes Symbol gewichtet.',
    'Ein vielfältiger Beutel entzieht ihr das Ziel — oder verkeilen Sie genau diese Walze.',
    {
      id: 'zinken:gezinkte-walze',
      source: 'zinken',
      tags: [],
      priority: 216,
      machines: ['bandit'],
      ruleText: 'Gezinkte Walze: eine Walze läuft gegen Sie.',
      queries: {
        odds: (ctx, p) => (ctx.stream === 'reels' ? p * 0.7 : p),
      },
    },
    ['zinken:der-abschoepfer'],
    ['manip:klemmkeil'],
  ),

  zinken(
    'zinken:die-kamera',
    'Die Kamera',
    'Eine Kamera über dem Automaten: der TILT-Zeiger springt doppelt so schnell.',
    'Spielen Sie sauber — oder bestechen Sie. Rütteln ist hier einfach teuer.',
    {
      id: 'zinken:die-kamera',
      source: 'zinken',
      tags: [],
      priority: 218,
      ruleText: 'Die Kamera: jeder Stoß zählt doppelt.',
      queries: {
        tiltScale: (_ctx, v) => v * 2,
      },
    },
    ['zinken:mueder-greifer'],
    ['ruettel-builds'],
  ),

  zinken(
    'zinken:der-abschoepfer',
    'Der Abschöpfer',
    'Zehn Prozent verschwinden in der Etagenstufe der Auszahlung.',
    'Brechen Sie ihn ab und schöpfen Sie selbst — als Charm dreht er sich um.',
    {
      id: 'zinken:der-abschoepfer',
      source: 'zinken',
      tags: [],
      priority: 220,
      ruleText: 'Der Abschöpfer: 10 % gehen in der Etagenstufe verloren.',
      queries: {
        stageValue: (ctx, v) => (ctx.stage === 'floor' ? v * 0.9 : v),
      },
    },
    ['zinken:gezinkte-walze'],
    ['item:brecheisen'],
  ),
];

/**
 * MANIPULATIONEN — your riggings. Same interface, opposite side of the table, and they
 * persist because cabinets travel with you (core-loop.md).
 */
export const MANIPULATIONEN: ItemDef[] = [
  {
    id: 'manip:magnet-unterm-tisch',
    name: 'Der Magnet unterm Tisch',
    kind: 'manipulation',
    rarity: 'rar',
    text: 'Ein versteckter Magnet unter dem Automaten. Wirkt immer — und tickt immer.',
    combos: ['magnetschuh', 'muenzschieber-lawine'],
    nombos: ['niedriger-verdacht'],
    modifier: {
      id: 'manip:magnet-unterm-tisch',
      source: 'manipulation',
      tags: ['Magnet'],
      priority: 120,
      ruleText: 'Der Magnet unterm Tisch: die Taschen ziehen an — und der Saal merkt es.',
      queries: {
        pocketWidth: (_ctx, v) => v * 1.3,
      },
      events: {
        // a resource-burn dial: it pays while it runs, and it runs while it pays
        onScore: (ctx) => ctx.api.verdacht(1, 'Magnet unterm Tisch'),
      },
    },
  },
  {
    id: 'manip:bohrloch',
    name: 'Bohrloch',
    kind: 'manipulation',
    rarity: 'gemein',
    text: 'Ein Loch in der Seitenwand: was seitlich verloren geht, rollt zum Nachbarn statt ins Nichts. Die Wand hält dafür weniger aus.',
    combos: ['der-deckel', 'zinsmuenze'],
    nombos: ['grosse-haufen'],
    modifier: {
      id: 'manip:bohrloch',
      source: 'manipulation',
      tags: [],
      priority: 60,
      machines: ['muenzschieber'],
      ruleText: 'Bohrloch: Seitenverluste rollen in den Saal — die Wanne ist dafür brüchig.',
      queries: {
        escape: () => 'room',
        // you drilled the bank; the bank is now easier to burst. That is the trade.
        limitScale: (_ctx, v) => v * 1.25,
      },
    },
  },
  {
    id: 'manip:gezinkte-feder',
    name: 'Gezinkte Feder',
    kind: 'manipulation',
    rarity: 'gemein',
    text: 'Eine zweite, härtere Raste im Abschussfederhaus: die Kugel kommt schärfer heraus.',
    combos: ['gummigeld'],
    nombos: ['schwermuenze'],
    modifier: {
      id: 'manip:gezinkte-feder',
      source: 'manipulation',
      tags: ['Gummi'],
      priority: 62,
      machines: ['pachinko'],
      ruleText: 'Gezinkte Feder: der Abschuss federt härter.',
      queries: {
        restitution: (_ctx, v) => Math.min(0.95, v * 1.35),
      },
    },
  },
  {
    id: 'manip:klemmkeil',
    name: 'Klemmkeil',
    kind: 'manipulation',
    rarity: 'gemein',
    text: 'Ein Keil in der dritten Walze: sie fällt zuletzt und langsamer — Ihr Anhaltefenster wird breiter.',
    combos: ['zinken:gezinkte-walze'],
    nombos: ['schnelle-zuege'],
    modifier: {
      id: 'manip:klemmkeil',
      source: 'manipulation',
      tags: ['Kleber'],
      priority: 64,
      machines: ['bandit'],
      ruleText: 'Klemmkeil: die dritte Walze hängt — das Anhaltefenster ist breiter.',
      queries: {
        odds: (ctx, p) => (ctx.stream === 'reels' && ctx.event === 'slip' ? p * 0.55 : p),
      },
    },
  },
];

/**
 * Turn a ZINKEN into the charm version the Brecheisen leaves in your bag. The rigging is
 * the same modifier, re-sourced and always discovered — the House's grammar, now yours.
 * `Der Abschöpfer` is the pointed one: as a charm his skim runs the other way.
 */
export function stolenCharm(zinkenId: string): ItemDef | null {
  const z = ZINKEN.find((x) => x.id === zinkenId);
  if (!z || !z.modifier) return null;
  const base = z.modifier;
  const id = `charm:${zinkenId.replace('zinken:', '')}`;
  const inverted = zinkenId === 'zinken:der-abschoepfer'
    ? { stageValue: (ctx: { stage: string }, v: number) => (ctx.stage === 'floor' ? v * 1.12 : v) }
    : null;
  return {
    id,
    name: `${z.name} (erbeutet)`,
    kind: 'charm',
    rarity: 'preis',
    text: `${z.text} — jetzt auf Ihrer Seite des Tisches.`,
    combos: [z.id],
    nombos: [],
    modifier: {
      ...base,
      id,
      source: 'charm',
      discovered: true,
      // a stolen rigging is no longer bound to the cabinet it was screwed to
      machines: undefined,
      ruleText: `${z.name} (erbeutet): ${z.text}`,
      queries: inverted
        ? ({ ...base.queries, ...inverted } as Modifier['queries'])
        : base.queries,
    },
  };
}
